import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spin, Checkbox, Divider } from 'antd';
import { withLocalize } from 'react-localize-redux';
import SignatureCanvas from '../inputs/SignatureCanvas';
import { GetWeddingContractDeclarationTemplateParams, SaveWeddingContractDeclaration } from '../../infra/requests/WeddingContractDeclarationTemplateRequests';

const parseTemplate = (text, parameters) => {
  let result = text;
  (parameters || []).forEach(({ parameter, value }) => {
    result = result.replace(new RegExp(`\\{\\{\\{${parameter}\\}\\}\\}`, 'g'), value != null ? value : '');
  });
  return result;
};

const WeddingContractDeclarationModal = ({ isOpen, reserveId, language, onConfirm, translate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [parsedText, setParsedText] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [signatureLines, setSignatureLines] = useState([]);
  const signatureRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !reserveId) return;

    const fetchParams = async () => {
      setLoading(true);
      try {
        const res = await GetWeddingContractDeclarationTemplateParams(reserveId, language);

        if (res?.success && res?.data) {
          const { template: tpl, parameters: params } = res.data;
          setTemplate(tpl);
          setParameters(params || []);
          setParsedText(parseTemplate(tpl?.text || '', params || []));
        } else {
          // No active template configured — skip modal immediately
          onConfirm(null);
        }
      } catch (e) {
        console.error(e);
        onConfirm(null);
      } finally {
        setLoading(false);
      }
    };

    fetchParams();
  }, [isOpen, reserveId, language]);

  const canConfirm = checkboxChecked && signatureLines.length > 0;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const signatureImage = signatureRef.current?.getImageData() || null;
      const res = await SaveWeddingContractDeclaration(reserveId, {
        signature: signatureLines,
        signatureImage,
        signedAt: new Date().toISOString(),
        templateId: template?._id,
        templateParameters: parameters
      });
      if (res?.success) {
        onConfirm(res.data?._id);
      } else {
        console.error('Erro ao guardar declaração:', res?.data);
      }
    } catch (e) {
      console.error('Erro ao guardar declaração:', e);
    } finally {
      setSaving(false);
    }
  };

  const footer = canConfirm ? (
    <Button type="primary" onClick={handleConfirm} loading={saving} disabled={saving}>
      {translate('CONFIRM')}
    </Button>
  ) : null;

  return (
    <Modal
      visible={isOpen}
      title={translate('CONTRACT_DECLARATION_TITLE')}
      closable={false}
      maskClosable={false}
      width={window.innerWidth >= 1024 ? 760 : '95%'}
      bodyStyle={{ overflowY: 'auto', maxHeight: '70vh' }}
      footer={footer}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          {parsedText && (
            <div
              style={{ marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: parsedText }}
            />
          )}

          <Divider />

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={checkboxChecked}
              onChange={e => setCheckboxChecked(e.target.checked)}
            >
              {translate('CONTRACT_DECLARATION_CHECKBOX')}
            </Checkbox>
          </div>

          {checkboxChecked && (
            <div>
              <p style={{ marginBottom: 8 }}><strong>{translate('CONTRACT_DECLARATION_SIGNATURE')}</strong></p>
              <SignatureCanvas
                ref={signatureRef}
                width={500}
                height={180}
                onChange={setSignatureLines}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

WeddingContractDeclarationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  reserveId: PropTypes.string,
  language: PropTypes.string,
  onConfirm: PropTypes.func.isRequired
};

export default withLocalize(WeddingContractDeclarationModal);
